'use client';

import Script from 'next/script';

/**
 * Google Tag Manager for the Kaympus marketing site.
 *
 * The container ID is NOT hardcoded: it comes from `NEXT_PUBLIC_GTM_ID` in
 * `frontend/.env` (git-ignored, so each environment sets its own). Because
 * it is a `NEXT_PUBLIC_` variable it is inlined at build time — changing
 * it needs a fresh `next build`, not just a restart. When the variable is
 * empty or missing this renders nothing, which is the safe default: a
 * placeholder ID would make every page fetch a container Google 404s.
 *
 * Rendered ONCE from `(landing)/layout.jsx`, and only on the main marketing
 * host (see the host check there). It used to live inside the home page
 * component, which meant pricing, blog, support and every other marketing
 * page shipped with no tracking at all. Never mount this on a school's
 * tenant host or on the logged-in app shell.
 *
 * `lazyOnload`, not `afterInteractive`: gtm.js plus the gtag.js it pulls in
 * are ~287KB of third-party JS, and on a throttled mobile profile they
 * landed in the same window as React's hydration (gtag alone was a 315ms
 * long task). Deferring until idle keeps them out of the tap-ready path.
 * page_view still fires, just a moment later — the standard trade.
 *
 * GA4 is configured INSIDE the container, so there is deliberately no
 * direct gtag.js snippet anywhere in the code. A second hardcoded copy once
 * loaded the same GA4 property twice and doubled every pageview. If GA4 is
 * ever removed from the container it has to be added back here, not left
 * to a snippet nobody remembers.
 *
 * Client-side route changes: GA4's enhanced measurement ("page changes
 * based on browser history events") records them; keep that enabled in the
 * GA4 property, or add a History Change trigger in GTM.
 */
const GTM_ID = (process.env.NEXT_PUBLIC_GTM_ID || '').trim();

export default function GoogleTagManager() {
    if (!/^GTM-[A-Z0-9]+$/.test(GTM_ID)) return null;

    return (
        <>
            <Script id="google-tag-manager" strategy="lazyOnload">
                {`
                    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                    })(window,document,'script','dataLayer','${GTM_ID}');
                `}
            </Script>
            <noscript>
                <iframe
                    src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
                    height="0"
                    width="0"
                    style={{ display: 'none', visibility: 'hidden' }}
                ></iframe>
            </noscript>
        </>
    );
}
